import type { ClientContactType, ClientContactWriteType } from '@econtractor/types';
import {
    CLIENT_CONTACT_TYPES,
    CLIENT_CONTACT_VALUE_LIMITS,
    normalizeContactValue,
    validateContactValue,
} from '@econtractor/types';

export {
    CLIENT_CONTACT_TYPES,
    CLIENT_CONTACT_VALUE_LIMITS,
    normalizeContactValue,
    validateContactValue,
};

export interface ClientContactTypeConfig {
    solarName?: string;
    imgSrc?: string;
    placeholderKey: ClientContactType;
    inputMode?: 'tel' | 'email' | 'url' | 'text';
    prefix?: string;
}

export const CLIENT_CONTACT_TYPE_CONFIG: Record<ClientContactType, ClientContactTypeConfig> = {
    phone: { solarName: 'telephone-outbound', placeholderKey: 'phone', inputMode: 'tel', prefix: '+' },
    email: { solarName: 'envelope', placeholderKey: 'email', inputMode: 'email' },
    instagram: { imgSrc: '/imgs/social/instagram.png', placeholderKey: 'instagram', inputMode: 'url', prefix: 'https://' },
    twitter: { solarName: 'share', placeholderKey: 'twitter', inputMode: 'url', prefix: 'https://' },
    facebook: { imgSrc: '/imgs/social/facebook.png', placeholderKey: 'facebook', inputMode: 'url', prefix: 'https://' },
    linkedin: { imgSrc: '/imgs/social/linkedin.png', placeholderKey: 'linkedin', inputMode: 'url', prefix: 'https://' },
    viber: { imgSrc: '/imgs/social/viber.png', placeholderKey: 'viber', inputMode: 'tel', prefix: '+' },
    whatsapp: { imgSrc: '/imgs/social/whatsapp.png', placeholderKey: 'whatsapp', inputMode: 'tel', prefix: '+' },
    telegram: { imgSrc: '/imgs/social/telegram.png', placeholderKey: 'telegram', inputMode: 'text', prefix: '@' },
    website: { solarName: 'globe2', placeholderKey: 'website', inputMode: 'url', prefix: 'https://' },
    other: { solarName: 'card-list', placeholderKey: 'other', inputMode: 'text' },
};

export const CLIENT_CONTACT_TYPE_LIST = CLIENT_CONTACT_TYPES.map((type) => ({
    type,
    ...CLIENT_CONTACT_TYPE_CONFIG[type],
}));

export const getContactStartWithFieldProps = (
    type: ClientContactType,
): { kind: 'plain' } | { kind: 'prefix'; startWith: string; onlyNumber?: boolean } => {
    switch (type) {
        case 'phone':
        case 'viber':
        case 'whatsapp':
            return { kind: 'prefix', startWith: '+', onlyNumber: true };
        case 'telegram':
            return { kind: 'prefix', startWith: '@' };
        case 'instagram':
        case 'twitter':
        case 'facebook':
        case 'linkedin':
        case 'website':
            return { kind: 'prefix', startWith: 'https://' };
        default:
            return { kind: 'plain' };
    }
};

export const createContactLocalKey = (): string => {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    return `contact-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

export interface ContactChannelItem {
    ID: number;
    contactType: ClientContactType;
    contactValue: string;
}

export type SaveContactChannelResult =
    | { ok: true; contact: ContactChannelItem }
    | { ok: false; errCode?: string };

export interface ContactsAdapter {
    list: (ownerId: number) => Promise<ContactChannelItem[] | null>;
    add: (ownerId: number, contact: ClientContactWriteType) => Promise<SaveContactChannelResult>;
    update: (ownerId: number, contactId: number, contactValue: string) => Promise<SaveContactChannelResult>;
    remove: (ownerId: number, contactId: number) => Promise<boolean>;
}

export interface ClientContactRow extends ContactChannelItem {
    localKey: string;
    clientId?: number;
}

export const toContactRow = (item: ContactChannelItem): ClientContactRow => ({
    ...item,
    localKey: item.ID > 0 ? `id-${item.ID}` : createContactLocalKey(),
});

export const buildContactHref = (
    contact: Pick<ContactChannelItem, 'contactType' | 'contactValue'>,
): string | undefined => {
    switch (contact.contactType) {
        case 'phone':
        case 'viber':
        case 'whatsapp':
            return `tel:${contact.contactValue}`;
        case 'email':
            return `mailto:${contact.contactValue}`;
        case 'telegram':
            return contact.contactValue.startsWith('@')
                ? `https://t.me/${contact.contactValue.slice(1)}`
                : contact.contactValue;
        case 'instagram':
        case 'twitter':
        case 'facebook':
        case 'linkedin':
        case 'website':
            return contact.contactValue.startsWith('http') ? contact.contactValue : undefined;
        default:
            return undefined;
    }
};
